import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";

/* ============================================================
   NEURAL NEXUS HERO — Phase 2
   Canvas: Particle Constellation + Mouse Attraction
   Headline: "Architecting Intelligence."
   Scroll Cue: Minimalist mouse icon with animated scroll wheel
   ============================================================ */

// Background colour matching CSS token: --background = 283 42% 6%
const BG_RGBA = "rgba(11, 5, 17, 0.07)";
const WASABI  = "#DFFF00";
const MINT    = "#00FFC2";

export const NeuralNexusHero = () => {
  const canvasRef  = useRef(null);
  const sectionRef = useRef(null);
  const mouseRef   = useRef({ x: -9999, y: -9999 }); // off-screen default
  const [ready, setReady] = useState(false);

  /* ---- Mouse tracking (relative to section) ---- */
  useEffect(() => {
    const section = sectionRef.current;
    if (!section) return;

    const onMove = (e) => {
      const r = section.getBoundingClientRect();
      mouseRef.current = { x: e.clientX - r.left, y: e.clientY - r.top };
    };
    const onLeave = () => { mouseRef.current = { x: -9999, y: -9999 }; };

    section.addEventListener("mousemove", onMove);
    section.addEventListener("mouseleave", onLeave);
    return () => {
      section.removeEventListener("mousemove", onMove);
      section.removeEventListener("mouseleave", onLeave);
    };
  }, []);

  /* ---- Particle constellation canvas ---- */
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    let raf;
    let time = 0;

    /* Resize — re-scatter on resize */
    const resize = () => {
      canvas.width  = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
    };
    resize();
    const ro = new ResizeObserver(resize);
    ro.observe(canvas);

    /* ---- Particles ---- */
    const N = 150;
    const SPEED_CAP = 1.2;   // maximum particle speed
    const MOUSE_R   = 220;   // attraction radius in px
    const MOUSE_F   = 0.08;  // attraction strength
    const CONN_D_SQ = 75 * 75; // connection max distance squared

    const mkParticle = () => ({
      x:    Math.random() * (canvas.width  || window.innerWidth),
      y:    Math.random() * (canvas.height || window.innerHeight),
      vx:   (Math.random() - 0.5) * 0.18,
      vy:   (Math.random() - 0.5) * 0.18,
      baseVx: 0, baseVy: 0, // set after init
      r:    0.9 + Math.random() * 1.4,
      type: Math.random() > 0.52 ? "w" : "m",
      a:    0.28 + Math.random() * 0.38,
      phi:  Math.random() * Math.PI * 2, // phase offset for size pulse
    });

    const pts = Array.from({ length: N }, () => {
      const p = mkParticle();
      p.baseVx = p.vx;
      p.baseVy = p.vy;
      return p;
    });

    /* Slow perlin-like flow field */
    const flowAngle = (x, y, t) => {
      const nx = x * 0.0018;
      const ny = y * 0.0018;
      return (
        Math.sin(nx + t * 0.28) * Math.cos(ny - t * 0.19) * Math.PI * 1.35
      );
    };

    /* ---- Render loop ---- */
    const tick = () => {
      /* Slow trail — particles linger to form constellation lines */
      ctx.fillStyle = BG_RGBA;
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      time += 0.003;

      const mx = mouseRef.current.x;
      const my = mouseRef.current.y;

      /* Update positions */
      for (const p of pts) {
        /* Flow field nudge */
        const angle = flowAngle(p.x, p.y, time);
        p.vx += Math.cos(angle) * 0.009;
        p.vy += Math.sin(angle) * 0.009;

        /* Mouse attraction — gentle, data-being-drawn feel */
        const dx = mx - p.x;
        const dy = my - p.y;
        const distSq = dx * dx + dy * dy;
        const dist   = Math.sqrt(distSq);

        if (dist < MOUSE_R && dist > 1) {
          const force = ((MOUSE_R - dist) / MOUSE_R) * MOUSE_F;
          p.vx += (dx / dist) * force;
          p.vy += (dy / dist) * force;
        }

        /* Drift back toward base velocity */
        p.vx += (p.baseVx - p.vx) * 0.015;
        p.vy += (p.baseVy - p.vy) * 0.015;

        /* Speed cap */
        const spd = Math.sqrt(p.vx * p.vx + p.vy * p.vy);
        if (spd > SPEED_CAP) {
          p.vx = (p.vx / spd) * SPEED_CAP;
          p.vy = (p.vy / spd) * SPEED_CAP;
        }

        p.x += p.vx;
        p.y += p.vy;

        /* Wrap around edges */
        if (p.x < -12) p.x = canvas.width  + 12;
        if (p.x > canvas.width  + 12) p.x = -12;
        if (p.y < -12) p.y = canvas.height + 12;
        if (p.y > canvas.height + 12) p.y = -12;

        /* Draw particle — brightens near mouse */
        const nearMouse = dist < MOUSE_R;
        const pulse = 0.75 + Math.sin(time * 1.1 + p.phi) * 0.25;
        const alpha  = p.a * pulse * (nearMouse ? 1.35 : 1);
        const radius = p.r * (nearMouse ? 1 + (1 - dist / MOUSE_R) * 0.8 : 1);

        ctx.beginPath();
        ctx.arc(p.x, p.y, radius, 0, Math.PI * 2);
        ctx.fillStyle = p.type === "w" ? WASABI : MINT;
        ctx.globalAlpha = Math.min(alpha, 0.85);
        ctx.fill();
        ctx.globalAlpha = 1;
      }

      /* Draw constellation connections */
      for (let i = 0; i < pts.length; i++) {
        for (let j = i + 1; j < pts.length; j++) {
          const ddx = pts[i].x - pts[j].x;
          const ddy = pts[i].y - pts[j].y;
          const dsq = ddx * ddx + ddy * ddy;

          if (dsq < CONN_D_SQ) {
            const d = Math.sqrt(dsq);
            ctx.beginPath();
            ctx.moveTo(pts[i].x, pts[i].y);
            ctx.lineTo(pts[j].x, pts[j].y);
            ctx.strokeStyle = pts[i].type === "w" ? WASABI : MINT;
            ctx.globalAlpha = (1 - d / 75) * 0.09;
            ctx.lineWidth   = 0.45;
            ctx.stroke();
            ctx.globalAlpha = 1;
          }
        }
      }

      raf = requestAnimationFrame(tick);
    };

    /* Tiny delay so canvas dimensions are committed before first tick */
    const timer = setTimeout(() => {
      setReady(true);
      tick();
    }, 80);

    return () => {
      clearTimeout(timer);
      cancelAnimationFrame(raf);
      ro.disconnect();
    };
  }, []);

  const scrollToProjects = () =>
    document.getElementById("projects")?.scrollIntoView({ behavior: "smooth" });

  /* ---- Framer Motion variants (no gate on canvas ready) ---- */
  const fadeUp = (delay = 0) => ({
    initial: { opacity: 0, y: 18 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.85, delay, ease: [0.22, 1, 0.36, 1] },
  });

  return (
    <section
      id="hero"
      ref={sectionRef}
      className="relative w-full min-h-screen flex items-center justify-center overflow-hidden"
    >
      {/* ---- Canvas ---- */}
      <canvas
        ref={canvasRef}
        className="absolute inset-0 w-full h-full"
        style={{ background: "transparent" }}
      />

      {/* Top vignette */}
      <div
        className="absolute top-0 inset-x-0 h-48 pointer-events-none"
        style={{
          background:
            "linear-gradient(to bottom, hsl(283 42% 6%), transparent)",
        }}
      />
      {/* Bottom vignette — blends into next section */}
      <div
        className="absolute bottom-0 inset-x-0 h-64 pointer-events-none"
        style={{
          background:
            "linear-gradient(to top, hsl(283 42% 6%) 20%, transparent)",
        }}
      />
      {/* Radial vignette on edges */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(ellipse 70% 80% at 50% 50%, transparent 55%, hsl(283 42% 5% / 0.8) 100%)",
        }}
      />

      {/* ---- Hero content ---- */}
      <div className="relative z-10 flex flex-col items-center text-center px-6 pt-20">

        {/* Eyebrow — horizontal rule + label + rule */}
        <motion.div
          {...fadeUp(0.25)}
          className="flex items-center gap-4 mb-10"
        >
          <span className="h-px flex-1 max-w-[60px]" style={{ background: "hsl(68 100% 50% / 0.45)" }} />
          <span className="font-mono text-xs tracking-[0.28em] uppercase" style={{ color: "hsl(68 100% 50% / 0.8)" }}>
            AI & Data Engineering &nbsp;&middot;&nbsp; Vibe Coder
          </span>
          <span className="h-px flex-1 max-w-[60px]" style={{ background: "hsl(68 100% 50% / 0.45)" }} />
        </motion.div>

        {/* Headline — massive, cinematic */}
        <motion.h1
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.1, delay: 0.42, ease: [0.16, 1, 0.3, 1] }}
          className="font-display font-bold leading-[0.95] tracking-tight mb-7"
          style={{ fontSize: "clamp(3.2rem, 8vw, 8.5rem)" }}
        >
          <span className="block" style={{ color: "hsl(var(--foreground))" }}>
            Architecting
          </span>
          <span className="block" style={{ color: "hsl(var(--primary))" }}>
            Intelligence.
          </span>
        </motion.h1>

        {/* Sub-headline */}
        <motion.p
          {...fadeUp(0.82)}
          className="font-mono text-xs tracking-[0.3em] uppercase mb-9"
          style={{ color: "hsl(var(--foreground-muted))", maxWidth: "28rem" }}
        >
          Edge AI &nbsp;&middot;&nbsp; Agentic Systems &nbsp;&middot;&nbsp; Production Infrastructure
        </motion.p>

        {/* CTAs */}
        <motion.div
          {...fadeUp(1.05)}
          className="flex flex-col sm:flex-row gap-4"
        >
          <button
            onClick={scrollToProjects}
            className="btn-wasabi px-11 py-3.5 rounded-lg text-xs uppercase tracking-[0.16em]"
          >
            View Work
          </button>
          <button
            onClick={() =>
              document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" })
            }
            className="btn-ghost-mint px-11 py-3.5 rounded-lg text-xs uppercase tracking-[0.16em]"
          >
            Start a Project
          </button>
        </motion.div>
      </div>
    </section>
  );
};

export default NeuralNexusHero;
